import { Fragment, useMemo } from "react";
import { SeasonBanner } from './SeasonBanner.js';
import type { CalendarSystem, CivilDate, Lang } from "@calendar/shared";
import { n } from "../format.js";
import { cornerDates, jalaliYearForGregorian, jalaliYearForHijri, monthDays, monthLabel, seasonOfMonth, weekDayNames, weekendFlags } from "../year-grid.js";
import { buildYearOccasions, type OccasionMap } from "../occasions.js";

interface Props {
  year: number;
  calendar: CalendarSystem;
  lang: Lang;
  t: (key: string, vars?: Record<string, string | number>) => string;
  onOpenDay: (day: CivilDate) => void;
  /** How many events fall on each day, keyed "year-month-day" of the active calendar. */
  eventCounts: Map<string, number>;
}

type CornerCalendar = "jalali" | "gregorian" | "hijri";

/**
 * Which two systems fill the bottom corners, per active calendar.
 *
 * The rule the user asked for: the active calendar is the large middle numeral,
 * and the other two sit opposite each other. With Jalali active, Gregorian is
 * bottom-left and Hijri bottom-right. With Gregorian active the two swap, so
 * Jalali takes the left corner. Hijri active keeps the same pairing as Jalali,
 * since Hijri is normally read as the secondary system on the right.
 */
const CORNER_LAYOUT: Record<CalendarSystem, [CornerCalendar, CornerCalendar]> = {
  jalali: ["gregorian", "hijri"],
  gregorian: ["jalali", "hijri"],
  hijri: ["gregorian", "jalali"],
};

/**
 * Year overview, laid out the way a printed wall calendar is: twelve month
 * blocks, each a plain grid of day numbers with no events listed.
 *
 * The active calendar's date is the large numeral in the middle of the cell and
 * the other two systems sit in the bottom corners, swapped so that switching
 * the active calendar moves the numbers the user expects to move.
 */
export function YearView({ year, calendar, lang, t, onOpenDay, eventCounts }: Props) {
  const weekdays = weekDayNames(calendar, lang);
  const [startCorner, endCorner] = CORNER_LAYOUT[calendar];
  const today = t("nav.today");

  // Occasions are published on the Jalali calendar, so the year on screen is
  // converted before the rule set is built and the result is re-keyed into
  // whatever the user is looking at. The whole build is guarded: an active
  // calendar switched mid-view can leave the anchor year outside the
  // converters' supported range, and a throwing celebration table must never
  // unmount the app.
  const occasions: OccasionMap = useMemo(() => {
    try {
      if (calendar === "jalali") return buildYearOccasions(year, calendar, lang);
      if (calendar === "gregorian") {
        // A Gregorian year runs Jan to Dec and so overlaps two Jalali years.
        return mergeOccasions(
          buildYearOccasions(jalaliYearForGregorian(year, 1), calendar, lang),
          buildYearOccasions(jalaliYearForGregorian(year, 12), calendar, lang),
        );
      }
      return buildYearOccasions(jalaliYearForHijri(year, 1), calendar, lang);
    } catch {
      return new Map();
    }
  }, [year, calendar, lang]);

  return (
    <div className="year" data-calendar={calendar}>
      <div className="year__months">
        {Array.from({ length: 12 }, (_, mi) => {
          const month = mi + 1;
          const days = monthDays({ year, month, day: 1 }, calendar);
          const off = weekendFlags(calendar, days);
          return (
            <Fragment key={month}>
            {mi % 3 === 0 && <SeasonBanner season={seasonOfMonth(year, month, calendar)} lang={lang} calendar={calendar} />}
            <section className="year__month">
              <h2 className="year__monthname">{monthLabel(month, calendar, lang)}</h2>
              <div className="year__week" aria-hidden="true">
                {weekdays.map((w, i) => (
                  <span className="year__weekday" key={i}>
                    {w.slice(0, 1)}
                  </span>
                ))}
              </div>
              <div className="year__grid">
                {days.map((day, di) => {
                  const inMonth = day.month === month;
                  const key = `${day.year}-${day.month}-${day.day}`;
                  const count = eventCounts.get(key) ?? 0;
                  const corners = cornerDates(day, calendar);
                  const occasion = inMonth ? occasions.get(key) : undefined;
                  return (
                    <button
                      type="button"
                      key={key}
                      className={[
                        "year__day",
                        inMonth ? "" : "year__day--out",
                        off[di] ? "year__day--off" : "",
                        count > 0 ? "year__day--busy" : "",
                        occasion ? "year__day--occasion" : "",
                        occasion?.holiday ? "year__day--holiday" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => inMonth && onOpenDay(day)}
                      disabled={!inMonth}
                      title={occasion ? occasion.name : undefined}
                      aria-label={occasion ? occasion.name : undefined}
                    >
                      <span className="year__big">{n(String(day.day), lang)}</span>
                      <span className="year__corner year__corner--start">
                        {n(String(corners[startCorner]), lang)}
                      </span>
                      <span className="year__corner year__corner--end">
                        {n(String(corners[endCorner]), lang)}
                      </span>
                      {count > 0 && <span className="year__dot" aria-hidden="true" />}
                    </button>
                  );
                })}
              </div>
            </section>
            </Fragment>
          );
        })}
      </div>
      <p className="year__legend muted">{t("year.hint")}</p>
      <p className="visually-hidden">{today}</p>
    </div>
  );
}

/**
 * Combine two years' occasion maps for a view that spans both of them.
 *
 * A holiday wins over an occasion, so a day touched by both is not left with
 * whichever map happened to be merged second.
 */
function mergeOccasions(first: OccasionMap, second: OccasionMap): OccasionMap {
  const merged: OccasionMap = new Map(first);
  for (const [key, occasion] of second) {
    const existing = merged.get(key);
    if (existing && existing.holiday && !occasion.holiday) continue;
    merged.set(key, occasion);
  }
  return merged;
}
